/* global Resources, Sys, Services, Utils */
Sys.ns("Services");

/**
 * The Bet Service handles all the bet settings.
 *
 * @class Services.Bet
 * @extends Services.Base
 */
Services.Bet = {
    constructor: function(config) {
        Services.Bet.superclass.constructor.apply(this, arguments);

        this._ignoreInitialBet = Services.localStorageManager.readData("ignoreInitialBet") || false;
        this._useCustomChanges = false;
        this._updateBetSettings(config.initResponse);
        this._allCoinValues = [];
        this._updateAllCoinValues();
    },

    _setupEvents: function() {
        this.observable.on({
            "request:bet.reduceBet": this.reduceBet.bind(this),
            "notify:freeRounds.ended": this._freeRoundsSettingsUpdate.bind(this),
            "notify:freeRounds.reInitResponseReceived": this._freeRoundsSettingsUpdate.bind(this)
        });
    },

    /**
     * Checks if there are multiple bet levels defined.
     *
     * @returns {boolean} Whether or not there are more than one bet level.
     */
    hasMultipleBetLevels: function() {
        return this._betLevels.length > 1;
    },

    /**
     * Gets the current bet in coins.
     *
     * @returns {number} The current bet in coins.
     */
    getBetCoins: function() {
        return this._betSize * this._betLevel;
    },

    /**
     * Gets the current bet in cash.
     *
     * @returns {number} The current bet in cents.
     */
    getBetCash: function() {
        return this._betSize * this._betLevel * this._denomination;
    },

    /**
     * Gets the available bet levels.
     *
     * @returns {number[]} The available bet levels.
     */
    getBetLevels: function() {
        return this._betLevels;
    },

    /**
     * Sets the available bet levels.
     *
     * @param {number[]} betLevels The bet levels.
     * @returns {void}
     */
    setBetLevels: function(betLevels) {
        this._betLevels = betLevels;
    },

    /**
     * Gets the current bet level.
     *
     * @returns {number} The current bet level.
     */
    getBetLevel: function() {
        return this._betLevel;
    },

    /**
     * Sets the current bet level.
     *
     * @param {number} betLevel The bet level.
     * @returns {void}
     */
    setBetLevel: function(betLevel) {
        if (this._betLevel === betLevel) {
            return;
        }
        this._betLevel = betLevel;

        Services.settingsManager.storeSetting("betLevel", betLevel, !Services.settingsManager.getSetting("inFreerounds"));

        this.publish("changed");
    },

    /**
     * Gets the current denomination.
     *
     * @returns {number} The current denomination.
     */
    getDenomination: function() {
        return this._denomination;
    },

    /**
     * Sets the current denomination.
     *
     * @param {number} denomination The denomination.
     * @returns {void}
     */
    setDenomination: function(denomination) {
        if (this._denomination === denomination) {
            return;
        }
        this._denomination = denomination;

        Services.settingsManager.storeSetting(
            "denomination",
            denomination,
            !Services.settingsManager.getSetting("inFreerounds")
        );

        this.publish("changed");
    },

    /**
     * Gets the available denominations.
     *
     * @returns {number[]} The available denominations.
     */
    getDenominations: function() {
        return this._denominations;
    },

    setDenominations: function(denominations) {
        this._denominations = denominations;
    },

    /**
     * Gets the current bet size (combined value of all betlines).
     *
     * @returns {number} The bet size in coins.
     */
    getBetSize: function() {
        return this._betSize;
    },

    /**
     * Set the current bet size.
     *
     * @param {number} value The denomination.
     * @returns {void}
     */
    setBetSize: function(value) {
        this._betSize = value;
        this.publish("changed");
    },

    /**
     * Requests the bet to be reduced by the player.
     *
     * @returns {void}
     */
    reduceBet: function() {
        this.publish("limitReached");
    },

    /**
     * Checks if the bet overlaps the set "if cash decreases by" limit.
     *
     * @returns {boolean} If the bet is overlapping.
     */
    isBetOverlapping: function() {
        var betCents = Services.moneyManager.getBetCents(),
            ifCashDecreases = Services.settingsManager.getSetting("stopAutoplayIfBalanceDecreasedBy"),
            isBetOverlapping = false;

        if (typeof ifCashDecreases === "number" && ifCashDecreases > 0) {
            isBetOverlapping = betCents > ifCashDecreases;
        }

        return isBetOverlapping;
    },

    /**
     * Gets the custom coin values provided in the query string or null if they are not among the initial values.
     *
     * @example
     * The values are taken from the query string that is on the format:
     *
     * customConfiguration.coinValues=1%252C5%252C100%252C25%252C50%252C100
     * @param {number[]} initialValues All denominations from server response.
     * @returns {Array} The array containing the custom coin values or empty array if they were invalid.
     */
    getCustomCoinValues: function(initialValues) {
        var queryData = Resources.readData("queryData");

        return Utils.Helpers.getCustomCoinValues(initialValues, queryData);
    },

    /**
     * Gets all coin values.
     *
     * @returns {Array} Coin values.
     */
    getAllCoinValues: function() {
        return this._allCoinValues;
    },

    /**
     * Checks if there are multiple coin values.
     *
     * @returns {boolean} Returns true if there are multiple coin values.
     */
    hasMultipleCoinValues: function() {
        return this._allCoinValues.length > 1;
    },

    /**
     * Gets the initial bet changes as an object containing the new default bet level and coin value or null
     * if the values are not among the coin values and bet levels provided.
     *
     * @example
     * The values are taken from the query string that is on the format:
     *
     * customConfiguration.defaultCoinValue=10&customConfiguration.defaultBetLevel=10
     *
     * @private
     * @param {number[]} coinValues The array of allowed coin values.
     * @param {number[]} betLevels The array of betLevels.
     * @returns {?Object} The object containing the new default coin value or null.
     */
    getCustomBetChanges: function(coinValues, betLevels) {
        var queryData = Resources.readData("queryData"),
            customBetLevel = parseInt(queryData["customConfiguration.defaultBetLevel"], 10),
            customCoinValue = parseInt(queryData["customConfiguration.defaultCoinValue"], 10),
            customBetChanges = {};

        if (Sys.isDefined(customBetLevel) && betLevels.indexOf(customBetLevel) !== -1) {
            customBetChanges.defaultBetLevel = customBetLevel;
        }

        if (Sys.isDefined(customCoinValue) && coinValues.indexOf(customCoinValue) !== -1) {
            customBetChanges.defaultCoinValue = customCoinValue;
        }

        return customBetChanges;
    },

    /**
     * Updates bet settings when free rounds have triggered a new init response.
     *
     * @private
     * @returns {void}
     */
    _freeRoundsSettingsUpdate: function() {
        this._updateBetSettings(Resources.readData("gameServerInitResponse"));
        this._updateAllCoinValues();
        this.publish("reInitDataUpdated");
    },

    /**
     * Updates the bet settings based on the server response.
     *
     * @param {Object} response The server response.
     * @returns {void}
     */
    _updateBetSettings: function(response) {
        var betSettings = this._getBetSettings(response);

        this.setBetLevel(betSettings.betLevel);
        this.setBetLevels(betSettings.betLevels);

        this.setDenominations(betSettings.denominations);
        this.setDenomination(betSettings.denomination);

        this._betSize = betSettings.betSize;

        this.publish("changed");
    },

    /**
     * Updates all coin values array.
     *
     * @private
     * @returns {void}
     */
    _updateAllCoinValues: function() {
        var i, allDenominations = this.getDenominations();

        this._allCoinValues = [];
        for (i = 0; i < allDenominations.length; i++) {
            this._allCoinValues.push(allDenominations[i] / 100);
        }
    },

    /**
     * Gets the bet settings.
     *
     * @private
     * @param {Object} initResponse The game server init response.
     * @returns {Object} The bet settings.
     */
    _getBetSettings: function(initResponse) {
        var betLevel = parseInt(initResponse["betlevel.standard"], 10),
            betLevels = initResponse["betlevel.all"]
                .split(",")
                .map(function(value) {
                    return parseInt(value, 10);
                }),
            denomination = parseInt(
                initResponse["denomination.standard"],
                10
            ),
            denominations = initResponse["denomination.all"]
                .split(",")
                .map(function(value) {
                    return parseInt(value, 10);
                }),
            betSize = initResponse["bl.standard"]
                .split(",")
                .reduce(function(accumulatedBetSize, currentValue, index) {
                    // "bl.standard" can be a number, bl.standard=243 (such as if game has betways) - or comma separated numbers from 0 to x; 0,1,2,3 ... x.
                    // We want to calculate betSize by accumulating the values of the "bl.i[x].coins" entries from the response data.
                    // So we split "bl.standard" by comma, iterate and use the index to get the value from the "bl.i[x].coins" response data.
                    // Ex: currentIndex is 0, then bl.i0.coins is 15. Add 15 to betSize.
                    return (
                        accumulatedBetSize +
                        parseInt(
                            initResponse["bl.i" + index + ".coins"],
                            10
                        )
                    );
                }, 0),
            storedBetLevel,
            storedDenomination,
            queryData = Resources.readData("queryData"),
            forceUseCustomCoinValues = queryData["customConfiguration.forceUseCustomCoinValues"],
            customCoinValues,
            customBetChanges;

        // Settings from local storage
        if (Services.localStorageManager.hasData("betLevel")) {
            storedBetLevel = Number(
                Services.localStorageManager.readData("betLevel")
            );

            if (betLevels.indexOf(storedBetLevel) !== -1) {
                betLevel = storedBetLevel;
            }
        }

        // Settings from local storage
        if (Services.localStorageManager.hasData("denomination")) {
            storedDenomination = Number(
                Services.localStorageManager.readData("denomination")
            );

            if (denominations.indexOf(storedDenomination) !== -1) {
                denomination = storedDenomination;
            }
        }

        customCoinValues = this.getCustomCoinValues(denominations);
        customBetChanges = this.getCustomBetChanges(customCoinValues, betLevels);
        Services.settingsManager.storeSetting("inFreerounds", parseInt(initResponse.freeRoundsLeft, 10) > 0);

        if (!(Services.settingsManager.getSetting("inFreerounds")) && customCoinValues.length && customBetChanges.defaultCoinValue) {
            denominations = customCoinValues;

            if (!this._ignoreInitialBet) {
                this._useCustomChanges = true;

                // We store a checker in localStorage Manager so to remember
                // if we reload and should ignore the query data parameters
                Services.localStorageManager.storeData("ignoreInitialBet", true.toString());
            }
        }

        if (forceUseCustomCoinValues && customBetChanges.defaultBetLevel) {
            betLevel = customBetChanges.defaultBetLevel;
        }
        // betlevel settings from server response
        else if (Sys.isDefined(initResponse["bet.betlevel"])) {
            betLevel = Number(initResponse["bet.betlevel"]);
        }
        else if (this._useCustomChanges && customBetChanges.defaultBetLevel) {
            betLevel = customBetChanges.defaultBetLevel;
        }
        else if (betLevels.indexOf(storedBetLevel) !== -1) {
            betLevel = storedBetLevel;
        }

        if (forceUseCustomCoinValues && customBetChanges.defaultCoinValue) {
            denomination = customBetChanges.defaultCoinValue;
        }
        // denomination settings from server response
        else if (Sys.isDefined(initResponse["bet.denomination"])) {
            denomination = Number(initResponse["bet.denomination"]);
        }
        else if (this._useCustomChanges && customBetChanges.defaultCoinValue) {
            denomination = customBetChanges.defaultCoinValue;
        }
        else if (denominations.indexOf(storedDenomination) !== -1) {
            denomination = storedDenomination;
        }
        else if (customBetChanges.defaultCoinValue && denominations.indexOf(storedDenomination) === -1) {
            denomination = denominations[0];
        }

        return {
            betLevels: betLevels,
            betLevel: betLevel,
            denomination: denomination,
            denominations: denominations,
            betSize: betSize
        };
    }
};

Services.Bet = Sys.extend(Services.Base, Services.Bet, "Services.Bet");
